import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Phone, Mail, MapPin, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-28 md:pt-36 pb-24">
      <section className="container-x">
        <p className="label-eyebrow">{t.contact.eyebrow}</p>
        <h1 className="h-display mt-6 max-w-3xl text-5xl md:text-7xl font-light text-[#0B192C]">
          {t.contact.title}
        </h1>
        <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-[#475569] font-light">
          {t.contact.subtitle}
        </p>
      </section>

      <section className="container-x mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 flex flex-col gap-4">
          <ContactCard icon={Phone} label={t.contact.phone} value="+47 401 57 531" href="tel:+4740157531" testid="contact-phone" />
          <ContactCard icon={Mail} label={t.contact.email} value="franksuhr73@gmail.com" href="mailto:franksuhr73@gmail.com" testid="contact-email-link" />
          <ContactCard icon={MapPin} label={t.contact.address} value={t.contact.address_value} testid="contact-address" />

          <div className="mt-4 overflow-hidden rounded-2xl border border-black/5 h-[320px]" data-testid="contact-map">
            <iframe
              title="Lofoten map"
              src="https://www.google.com/maps?q=Svolv%C3%A6r%2C%20Lofoten%2C%20Norway&output=embed"
              className="w-full h-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          onSubmit={onSubmit}
          className="lg:col-span-7 card-soft p-8 md:p-10"
          data-testid="contact-form"
        >
          <h2 className="h-display text-2xl md:text-3xl font-medium text-[#0B192C]">
            {t.contact.form_title}
          </h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="flex flex-col gap-2">
              <Label htmlFor="cname" className="text-xs uppercase tracking-[0.18em] text-[#475569]">
                {t.contact.form_name}
              </Label>
              <Input id="cname" value={form.name} onChange={update("name")} required className="rounded-xl border-black/10 h-12" data-testid="contact-name" />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="cemail" className="text-xs uppercase tracking-[0.18em] text-[#475569]">
                {t.contact.form_email}
              </Label>
              <Input id="cemail" type="email" value={form.email} onChange={update("email")} required className="rounded-xl border-black/10 h-12" data-testid="contact-email" />
            </div>
            <div className="flex flex-col gap-2 md:col-span-2">
              <Label htmlFor="csubject" className="text-xs uppercase tracking-[0.18em] text-[#475569]">
                {t.contact.form_subject}
              </Label>
              <Input id="csubject" value={form.subject} onChange={update("subject")} className="rounded-xl border-black/10 h-12" data-testid="contact-subject" />
            </div>
            <div className="flex flex-col gap-2 md:col-span-2">
              <Label htmlFor="cmessage" className="text-xs uppercase tracking-[0.18em] text-[#475569]">
                {t.contact.form_message}
              </Label>
              <Textarea id="cmessage" value={form.message} onChange={update("message")} required rows={6} className="rounded-xl border-black/10" data-testid="contact-message" />
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-60" data-testid="contact-submit">
              {submitting ? t.contact.sending : t.contact.form_submit}
              {!submitting && <ArrowRight size={16} />}
            </button>
          </div>
        </motion.form>
      </section>
    </div>
  );
};

const ContactCard = ({ icon: Icon, label, value, href, testid }) => {
  const content = (
    <div className="flex items-start gap-4 p-6 rounded-2xl border border-black/5 bg-white hover:border-[#0B192C]/20 transition-colors">
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#F7F5F0] text-[#0B192C]">
        <Icon size={16} />
      </span>
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">{label}</p>
        <p className="mt-1 text-[#0B192C]">{value}</p>
      </div>
    </div>
  );
  return href ? (
    <a href={href} data-testid={testid}>
      {content}
    </a>
  ) : (
    <div data-testid={testid}>{content}</div>
  );
};

export default Contact;
